import React from 'react'
import { Box, SxProps, Theme } from '@mui/material'
import { innerRadius } from 'settings/brandRadius'

export interface PreviewCardProps {
  children: React.ReactNode
  sx?: SxProps<Theme>
  onClick?: () => void
}

/**
 * The one bordered container every "entity preview" row sits in — product, recipient,
 * owner, payment method, address.
 *
 * VERA-304: extracted because the Gift sheet showed two of these side by side with
 * DIFFERENT corner radii — the Item row came from `ProductCard` (`borderRadius: 1`),
 * the Recipient row was a hand-rolled `Box` (`borderRadius: 2`), and the empty-state
 * search button a third value (`1.5`). Three components each deciding their own
 * border, radius and padding is exactly the drift §36/§42 exist to stop: the values
 * are only "the same" until someone edits one of them.
 *
 * This owns the CONTAINER only — border, radius, padding, and the row layout
 * (centred, 16px gap). What goes inside stays the caller's business: `ProductCard`
 * puts a thumbnail + title, the recipient row puts `UserPreview` + a remove control.
 * That split is deliberate; a card that also owned its contents would need a prop per
 * entity type and would stop being reusable.
 */
export const PreviewCard: React.FC<PreviewCardProps> = ({ children, sx, onClick }) => (
  <Box
    onClick={onClick}
    sx={[
      theme => ({
        p: 2,
        // The INNER step, not the numeric `1`. A numeric sx radius is multiplied by the
        // tenant's Studio token at a factor nobody chose (VERA-315), so `1` was 8px on the
        // default brand and 10px on one that picked 10 — drift, not intent. The fix for
        // that was a literal 8px, which then tracked the brand not at all (VRG-562).
        // `innerRadius` is the third thing: 8px at the default as before, moving with the
        // brand above it. NOT the container step: at the ~80px height these rows run,
        // double reads as a pill.
        borderRadius: innerRadius(theme),
        border: `1px solid ${theme.palette.grey[300]}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        ...(onClick ? { cursor: 'pointer' } : null),
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {children}
  </Box>
)

export default PreviewCard
